// The hosted Ella chat embed (chat.aheadhealth.com).
//
// marketing-site injects the loader `${EMBED_ORIGIN}/ella/v1.js` once and drives
// the widget through `window.Ella`. The widget is an iframe served from the host
// app, so conversation traffic, session, the /api/ella proxy, pricing and
// analytics all live INSIDE the iframe — the site only injects the loader and
// tells it which page the visitor is on.
//
// Unlike booking (trigger-only, `launcher: false`), marketing-site uses
// `launcher: true`: the loader renders its OWN bubble + teaser + panel, so the
// site renders no chat UI of its own once the loader is injected (see
// chat-launcher.tsx — it returns null post-consent to avoid a double bubble).
//
// Config is passed via `window.ellaSettings` (the loader's SPA path — a
// dynamically-injected script has no `document.currentScript` to read
// `data-ella-*` attributes from). The loader reads it once at load.
//
// Analytics ride along for free: the embed forwards its `chat_*` events over the
// bridge into the host's `window.posthog`. This app pins its own posthog-js
// instance onto `window.posthog` (lib/posthog.ts), so site + chat land in one
// cross-surface funnel with no mapper on this side.

import { EXPERIMENTS } from "@/lib/experiments"

const MOBILE_TEASER_FLAG = EXPERIMENTS["chat-mobile-teaser-question-v1"].flag

export interface EllaSettings {
  country: string
  locale: string
  scope: string
  // The starter-prompt selector, separate from `scope` (which also drives
  // backend routing). Effective prompt scope on the host = `promptScope ?? scope`
  // — omit it to fall back to `scope`'s prompts.
  promptScope?: string
  // What the page the loader starts on sells (`lib/go-page-products.ts`). The
  // widget sends it to the chat backend with each message; a route change
  // replaces it through `setEllaScope`.
  pageProducts?: readonly string[]
  launcher?: boolean
  linksOrigin?: string
  origin?: string
  // Mobile launcher teaser: the generic greeting pill (default) or one
  // page-specific question that sends on tap. Read by the loader once at load.
  mobileTeaser?: "greeting" | "question"
  // The experiment arm behind `mobileTeaser`. The loader stamps `variant` as
  // `prompt_variant` on its teaser events and captures `$feature_flag_called`
  // itself when the mobile teaser shows — marketing-site fires no exposure.
  experiment?: { flag: string; variant: string }
}

/**
 * The teaser settings for an arm of `chat-mobile-teaser-question-v1`.
 *
 * `undefined` (arm not resolved yet) yields neither field, so a loader that
 * reads `window.ellaSettings` at that moment falls back to the greeting with no
 * experiment stamp rather than a mislabelled one. Any arm other than
 * `question` shows the greeting but is still stamped, so control is measured.
 */
export function mobileTeaserSettings(
  arm: string | undefined
): Pick<EllaSettings, "mobileTeaser" | "experiment"> {
  if (arm === undefined) return {}
  return {
    mobileTeaser: arm === "question" ? "question" : "greeting",
    experiment: { flag: MOBILE_TEASER_FLAG, variant: arm },
  }
}

type EllaApi = {
  open(): void
  close(): void
  // Updates the shown starter prompts + routing scope live, without dropping the
  // conversation. Added host-side in customer-support (nick/ella-embed-setscope).
  // OPTIONAL because a deployed loader predating that change has no such method —
  // callers must feature-detect (see `setEllaScope`), or the call throws
  // `TypeError: e.setScope is not a function` instead of the intended no-op.
  // `promptScope` is optional; the host's effective prompt scope is
  // `promptScope ?? scope`. `pageProducts` is what the page now sells; a loader
  // predating it ignores the extra argument.
  setScope?(
    scope: string,
    promptScope?: string,
    pageProducts?: readonly string[]
  ): void
  // Open the panel and send a starter prompt in one action — an inline chip on
  // the page seeds the conversation, stamped with `promptKey` (the host stamps it
  // as the trace prompt_key). Added host-side in customer-support
  // (nick/ella-embed-prompt-seed). OPTIONAL for the same reason as `setScope` —
  // callers must feature-detect (see `openEllaWithPrompt`).
  promptOpen?(text: string, promptKey?: string): void
  // Move the widget to another market/language: country + locale are baked
  // into the iframe URL at load, so the loader reloads the iframe on the new
  // ones (AHD-13379). OPTIONAL like the two above — a loader predating it has
  // no such method, and the iframe then stays on the market it loaded with.
  setContext?(country: string, locale: string): void
  // Hide or show the widget iframe without dropping the conversation. Added
  // host-side in customer-support. OPTIONAL like the three above — a deployed
  // loader predating it has no such method, and callers must feature-detect
  // (see `setEllaVisible`).
  setVisible?(visible: boolean): void
  identify(props: Record<string, unknown>): void
}

declare global {
  interface Window {
    ellaSettings?: EllaSettings
    Ella?: EllaApi
  }
}

export const EMBED_ORIGIN =
  process.env.NEXT_PUBLIC_ELLA_EMBED_ORIGIN || "https://chat.aheadhealth.com"

// marketing-site IS aheadhealth.com — the same origin the widget's in-panel
// product-card links point at — so the loader keeps card links same-origin.
const LINKS_ORIGIN = "https://aheadhealth.com"

// Module-level so injection is idempotent across every ResolvedChatWidget render
// and every SPA navigation: the loader is injected at most once per document.
let injected = false

// Actions requested before `window.Ella` exists, replayed in order once the
// loader's script has loaded and defined the global.
const pending: Array<(ella: EllaApi) => void> = []

function flushPending(): void {
  if (!window.Ella) return
  while (pending.length > 0) {
    const fn = pending.shift()
    fn?.(window.Ella)
  }
}

// Run `fn` now if the loader is ready, else buffer it until the script loads.
function runOrQueue(fn: (ella: EllaApi) => void): void {
  if (typeof window === "undefined") return
  if (window.Ella) fn(window.Ella)
  else pending.push(fn)
}

/**
 * Inject the Ella embed loader (once) with the given settings.
 *
 * Sets `window.ellaSettings` on every call so a loader that has not yet loaded
 * reads the freshest scope at load; injects `${EMBED_ORIGIN}/ella/v1.js` only
 * the first time. A loaded widget whose country or locale changed is moved over
 * with `setContext`. On the script's `load` event it flushes any actions buffered
 * before the global existed (an `openElla()` / `setEllaScope()` issued during
 * the same click).
 */
export function ensureEllaLoaded(settings: EllaSettings): void {
  if (typeof window === "undefined") return

  const previous = window.ellaSettings
  window.ellaSettings = {
    launcher: true,
    linksOrigin: LINKS_ORIGIN,
    origin: EMBED_ORIGIN,
    ...settings,
  }

  // Already loaded (a prior injection finished) — nothing to inject, but replay
  // anything that queued in the gap.
  if (window.Ella) {
    // A market/language switch is a soft `router.push`, so the loader and its
    // iframe outlive it — still on the old country + locale, which pick the
    // crisis number and the pricing gate (AHD-13379). The remounted launcher
    // lands here with the new ones: move the widget over.
    if (
      previous &&
      (previous.country !== settings.country ||
        previous.locale !== settings.locale)
    ) {
      window.Ella.setContext?.(settings.country, settings.locale)
    }
    // A remount on a chat country after a non-chat country hid the widget
    // (ChatHidden) must show it again — the loader outlives the React tree,
    // so nothing else would.
    window.Ella.setVisible?.(true)
    flushPending()
    return
  }

  if (injected) return
  injected = true

  const script = document.createElement("script")
  script.src = `${EMBED_ORIGIN}/ella/v1.js`
  script.async = true
  // The loader defines `window.Ella` synchronously as it evaluates, so by the
  // load event the global is present — replay the buffered actions then.
  script.addEventListener("load", flushPending)
  document.body.appendChild(script)
}

/**
 * Whether the loader is already on this page — injected by an earlier mount, or
 * already defined. The loader owns its bubble on `window` and outlives any React
 * tree, so a launcher that remounts (a language switch remounts the `[locale]`
 * layout, AHD-12803) must not paint its facade next to it.
 */
export function isEllaInjected(): boolean {
  if (typeof window === "undefined") return false
  return injected || window.Ella !== undefined
}

/** Open the chat panel (buffered until the loader is ready). */
export function openElla(): void {
  runOrQueue((ella) => ella.open())
}

/** Close the chat panel (buffered until the loader is ready). */
export function closeElla(): void {
  runOrQueue((ella) => ella.close())
}

/**
 * Hide or show the widget iframe without dropping the conversation (buffered
 * until the loader is ready). A no-op when the loader was never injected on
 * this page — there is nothing to hide, and queueing a hide would surface
 * against a later load. Feature-detected: a loader predating `setVisible`
 * simply stays visible.
 */
export function setEllaVisible(visible: boolean): void {
  if (!isEllaInjected()) return
  runOrQueue((ella) => ella.setVisible?.(visible))
}

/**
 * Update the widget's scope live — the routing scope, (optionally) the prompt
 * scope and what the page sells — as the visitor navigates the SPA, without
 * dropping the conversation. Also updates `window.ellaSettings` so a loader
 * still mid-load picks up the latest values at load, and buffers the `setScope`
 * call until the global exists.
 *
 * `pageProducts` belongs to the page, like the scope: pass the new page's list
 * on every call, and nothing for a page that sells none. The call then has the
 * two arguments it always had, and the widget forgets the previous page's.
 */
export function setEllaScope(
  scope: string,
  promptScope?: string,
  pageProducts?: readonly string[]
): void {
  const products = pageProducts?.length ? pageProducts : undefined
  if (typeof window !== "undefined" && window.ellaSettings) {
    window.ellaSettings.scope = scope
    window.ellaSettings.promptScope = promptScope
    window.ellaSettings.pageProducts = products
  }
  runOrQueue((ella) =>
    products
      ? ella.setScope?.(scope, promptScope, products)
      : ella.setScope?.(scope, promptScope)
  )
}

/**
 * Open the chat panel and send a starter prompt in one action (buffered until
 * the loader is ready). Seeds the conversation from an inline chip — `promptKey`
 * is the chip's provenance, stamped host-side into the trace as the prompt_key.
 *
 * Degrades gracefully: a deployed loader predating `promptOpen` (a host rollback)
 * still OPENS the panel via the always-present `open()` — an unseeded chat the
 * visitor can type into — rather than leaving a dead button (the facade already
 * gone, nothing shown). The prod host ships `promptOpen`, so this is a rollback
 * safety net, not the normal path.
 */
export function openEllaWithPrompt(text: string, promptKey?: string): void {
  runOrQueue((ella) => {
    if (ella.promptOpen) ella.promptOpen(text, promptKey)
    else ella.open()
  })
}
